import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Lock, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { app_name, logo_data_url } = useBranding();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) { toast.error("Password must be ≥ 8 characters"); return; }
    if (pw1 !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      toast.success("Password reset — please sign in");
      setTimeout(() => navigate("/login", { replace: true }), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset failed");
    }
    setBusy(false);
  };
  const head = (app_name || "BizAssist Online").trim().split(" ")[0];

  return (
    <div className="min-h-screen w-full grid place-items-center bg-[var(--canvas)] p-4" data-testid="reset-page">
      <div className="w-full max-w-md bg-[var(--surface)] border border-[var(--line)] rounded-sm shadow-lg">
        <div className="px-7 pt-7 pb-5 border-b border-[var(--line)] flex items-center gap-3">
          <div className="h-12 w-12 rounded-sm bg-[var(--ink)] grid place-items-center overflow-hidden">
            {logo_data_url
              ? <img src={logo_data_url} alt={head} className="h-9 w-9 object-contain" />
              : <img src="/bizassist-logo.png?v=2" alt={head} className="h-9 w-9 object-contain" />}
          </div>
          <div>
            <div className="font-display text-xl tracking-tight">{head}<span className="text-[var(--accent)]">.</span></div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mt-0.5">Reset password</div>
          </div>
        </div>
        <div className="px-7 py-6">
          {!token && (
            <div className="text-[13px] text-[var(--negative)]">Missing reset token. <Link to="/login" className="text-[var(--accent)] underline">Back to sign in</Link></div>
          )}
          {token && done && (
            <div className="flex items-center gap-2 text-[var(--positive)] text-[14px]"><CheckCircle2 size={18} /> Password updated. Redirecting to sign-in…</div>
          )}
          {token && !done && (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center gap-1.5"><Lock size={11} /> New password</label>
                <div className="relative">
                  <input data-testid="reset-pw1" type={show ? "text" : "password"} value={pw1} onChange={(e) => setPw1(e.target.value)} className="w-full mt-1 px-3 h-10 border border-[var(--line-2)] focus:border-[var(--ink-2)] bg-[var(--surface)] outline-none rounded-sm text-sm pr-10" autoFocus />
                  <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--ink)] mt-0.5" tabIndex={-1}>
                    {show ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <div className="text-[10.5px] text-[var(--muted)] mt-1">Min 8 characters.</div>
              </div>
              <div>
                <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center gap-1.5"><Lock size={11} /> Confirm password</label>
                <input data-testid="reset-pw2" type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full mt-1 px-3 h-10 border border-[var(--line-2)] focus:border-[var(--ink-2)] bg-[var(--surface)] outline-none rounded-sm text-sm" />
              </div>
              <button type="submit" disabled={busy} data-testid="reset-submit" className="w-full h-10 bg-[var(--accent)] hover:opacity-90 text-white font-medium rounded-sm text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} Update password
              </button>
              <Link to="/login" className="block text-center text-[12px] text-[var(--muted)] hover:text-[var(--ink)]">Back to sign in</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
